import { useEffect, useRef, useState, ReactNode } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { mainSiteUrl } from '@/mocks/categories';

interface ArticleLayoutProps {
  title: string;
  metaDescription: string;
  publishedDate: string;
  readTime: string;
  breadcrumb: { label: string; href: string }[];
  children: ReactNode;
}

export default function ArticleLayout({
  title,
  metaDescription,
  publishedDate,
  readTime,
  breadcrumb,
  children,
}: ArticleLayoutProps) {
  const [activeHeading, setActiveHeading] = useState('');
  const contentRef = useRef<HTMLDivElement>(null);

  // Build TOC from h2 headings in children (via DOM after render)
  const [headings, setHeadings] = useState<{ id: string; text: string }[]>([]);

  useEffect(() => {
    if (contentRef.current) {
      const h2s = contentRef.current.querySelectorAll('h2');
      const hItems: { id: string; text: string }[] = [];
      h2s.forEach((h2) => {
        if (!h2.id) {
          h2.id = h2.textContent?.toLowerCase().replace(/[^a-z0-9äöüß]+/g, '-').replace(/-$/, '') || '';
        }
        hItems.push({ id: h2.id, text: h2.textContent || '' });
      });
      setHeadings(hItems);
    }
  }, [children]);

  // Scroll spy
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveHeading(entry.target.id);
          }
        });
      },
      { rootMargin: '-80px 0px -60% 0px', threshold: 0 }
    );

    const h2s = contentRef.current?.querySelectorAll('h2');
    h2s?.forEach((h2) => observer.observe(h2));

    return () => observer.disconnect();
  }, [headings]);

  return (
    <>
      <Navbar />
      <main className="bg-background-50 min-h-screen">
        {/* Breadcrumb */}
        <div className="mx-auto max-w-[1440px] px-4 md:px-6 pt-6">
          <nav className="flex items-center gap-2 text-xs text-foreground-400 flex-wrap" aria-label="Breadcrumb">
            {breadcrumb.map((item, i) => (
              <span key={i} className="flex items-center gap-2">
                {i > 0 && <i className="ri-arrow-right-s-line text-[10px]"></i>}
                {i < breadcrumb.length - 1 ? (
                  <a href={item.href} className="hover:text-foreground-700 transition-colors cursor-pointer">
                    {item.label}
                  </a>
                ) : (
                  <span className="text-foreground-600">{item.label}</span>
                )}
              </span>
            ))}
          </nav>
        </div>

        <div className="mx-auto max-w-[1440px] px-4 md:px-6 py-8 md:py-12">
          <div className="flex flex-col lg:flex-row gap-10 lg:gap-16">
            {/* Article content */}
            <article ref={contentRef} className="lg:w-[65%]">
              {/* Article header */}
              <header className="mb-10">
                <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 leading-tight mb-4">
                  {title}
                </h1>
                <div className="flex items-center gap-4 text-xs text-foreground-400 mb-4">
                  <span className="flex items-center gap-1.5">
                    <i className="ri-calendar-line"></i>
                    {publishedDate}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-time-line"></i>
                    {readTime}
                  </span>
                </div>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                  {metaDescription}
                </p>
              </header>

              {/* Article body */}
              <div className="prose-custom text-foreground-800 leading-relaxed">
                {children}
              </div>

              {/* Back to home */}
              <div className="mt-12 pt-8 border-t border-background-200/70">
                <a
                  href="/"
                  className="inline-flex items-center gap-2 text-sm text-foreground-500 hover:text-foreground-900 transition-colors cursor-pointer"
                >
                  <i className="ri-arrow-left-line"></i>
                  Zurück zur Startseite
                </a>
              </div>
            </article>

            {/* Right sidebar */}
            <aside className="lg:w-[35%]">
              <div className="sticky top-24">
                {/* TOC */}
                {headings.length > 0 && (
                  <div className="bg-accent-600 rounded-2xl p-6 mb-6">
                    <h4 className="text-sm font-semibold text-background-50/90 mb-4 flex items-center gap-2">
                      <i className="ri-list-unordered"></i>
                      Inhaltsverzeichnis
                    </h4>
                    <nav className="space-y-2">
                      {headings.map((h) => (
                        <a
                          key={h.id}
                          href={`#${h.id}`}
                          className={`block text-sm transition-colors pl-3 border-l-2 ${
                            activeHeading === h.id
                              ? 'text-background-50 border-accent-400'
                              : 'text-background-50/50 border-transparent hover:text-background-50/80'
                          } cursor-pointer`}
                        >
                          {h.text}
                        </a>
                      ))}
                    </nav>
                  </div>
                )}

                {/* CTA */}
                <div className="bg-background-100 rounded-2xl p-6">
                  <h4 className="font-heading text-lg font-semibold text-foreground-950 mb-3">
                    Hol dir jetzt das OOPBuy Spreadsheet
                  </h4>
                  <p className="text-xs text-foreground-500 mb-4">
                    Alle besten Produkte und Verkäufer an einem Ort.
                  </p>
                  <a
                    href={mainSiteUrl}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center justify-center gap-2 w-full px-5 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap"
                  >
                    Zum Spreadsheet
                    <i className="ri-arrow-right-line"></i>
                  </a>
                </div>

                {/* Related links */}
                <div className="bg-background-100 rounded-2xl p-6 mt-4">
                  <h4 className="text-sm font-semibold text-foreground-950 mb-3">
                    Verwandte Artikel
                  </h4>
                  <div className="space-y-2">
                    <a href="/artikel/oopbuy-spreadsheet-guide" className="block text-sm text-foreground-500 hover:text-foreground-900 transition-colors cursor-pointer">
                      OOPBuy Spreadsheet Guide
                    </a>
                    <a href="/landing/beste-schuhe" className="block text-sm text-foreground-500 hover:text-foreground-900 transition-colors cursor-pointer">
                      Beste Schuhe 2026
                    </a>
                    <a href="/landing/beste-hoodies" className="block text-sm text-foreground-500 hover:text-foreground-900 transition-colors cursor-pointer">
                      Beste Hoodies 2026
                    </a>
                    <a href="/landing/beste-verkaeufer" className="block text-sm text-foreground-500 hover:text-foreground-900 transition-colors cursor-pointer">
                      Beste Verkäufer 2026
                    </a>
                    <a href="/" className="block text-sm text-foreground-500 hover:text-foreground-900 transition-colors cursor-pointer">
                      OOPBuy Spreadsheet Deutschland
                    </a>
                  </div>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}